import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginCredentials,
  RegisterData,
  AuthUser,
  Plan,
  Store,
  Employee,
  Customer,
  Reward,
  PointsTransaction,
  StoreFormData,
  EmployeeFormData,
  CustomerFormData,
  RewardFormData,
  AddPointsData,
  StorePointsSettingsData,
  RedeemRewardData,
  DashboardData
} from '../types';

/**
 * Serviço de API para comunicação com o backend Laravel
 */
class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor para adicionar token de autenticação
    this.api.interceptors.request.use((config) => {
      // Verificar primeiro se há um token de admin, depois o token regular
      const adminToken = localStorage.getItem('admin_token');
      const authToken = localStorage.getItem('auth_token');
      const url = config.url || '';
      const isAdminEndpoint = url.startsWith('/admin') || url.includes('/admin/');
      const token = isAdminEndpoint ? (adminToken || authToken) : (authToken || adminToken);

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor para tratamento de erros
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Só redireciona automaticamente se não estivermos na página de login
        if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ============= AUTENTICAÇÃO =============

  async register(data: RegisterData): Promise<ApiResponse<AuthUser>> {
    const response: AxiosResponse<ApiResponse<AuthUser>> = await this.api.post('/auth/register', data);
    return response.data;
  }

  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthUser>> {
    const response: AxiosResponse<ApiResponse<AuthUser>> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async employeeLogin(credentials: LoginCredentials): Promise<ApiResponse<AuthUser>> {
    const response: AxiosResponse<ApiResponse<AuthUser>> = await this.api.post('/auth/employee-login', credentials);
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/logout');
    return response.data;
  }

  async me(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/auth/me');
    return response.data;
  }

  // ============= PASSWORD RESET =============

  async forgotPassword(email: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/password/forgot', { email });
    return response.data;
  }

  async forgotPasswordEmployee(email: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/password/forgot-employee', { email });
    return response.data;
  }

  async resetPassword(data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/password/reset', data);
    return response.data;
  }

  async resetPasswordEmployee(data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/password/reset-employee', data);
    return response.data;
  }

  // ============= PLANOS =============

  async getPlans(): Promise<ApiResponse<Plan[]>> {
    const response: AxiosResponse<ApiResponse<Plan[]>> = await this.api.get('/plans');
    return response.data;
  }

  async getPlan(id: number): Promise<ApiResponse<Plan>> {
    const response: AxiosResponse<ApiResponse<Plan>> = await this.api.get(`/plans/${id}`);
    return response.data;
  }

  async createPlan(data: Partial<Plan>): Promise<ApiResponse<Plan>> {
    const response: AxiosResponse<ApiResponse<Plan>> = await this.api.post('/admin/plans', data);
    return response.data;
  }

  async updatePlan(id: number, data: Partial<Plan>): Promise<ApiResponse<Plan>> {
    const response: AxiosResponse<ApiResponse<Plan>> = await this.api.put(`/admin/plans/${id}`, data);
    return response.data;
  }

  async deletePlan(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/admin/plans/${id}`);
    return response.data;
  }

  // ============= LOJAS =============

  async getStores(): Promise<ApiResponse<Store[]>> {
    const response: AxiosResponse<ApiResponse<Store[]>> = await this.api.get('/stores');
    return response.data;
  }

  async getStore(id: number): Promise<ApiResponse<Store>> {
    const response: AxiosResponse<ApiResponse<Store>> = await this.api.get(`/stores/${id}`);
    return response.data;
  }

  async createStore(data: StoreFormData): Promise<ApiResponse<Store>> {
    const response: AxiosResponse<ApiResponse<Store>> = await this.api.post('/stores', data);
    return response.data;
  }

  async updateStore(id: number, data: Partial<StoreFormData>): Promise<ApiResponse<Store>> {
    const response: AxiosResponse<ApiResponse<Store>> = await this.api.put(`/stores/${id}`, data);
    return response.data;
  }

  async deleteStore(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/stores/${id}`);
    return response.data;
  }

  async getStoreDashboard(id: number): Promise<ApiResponse<DashboardData>> {
    const response: AxiosResponse<ApiResponse<DashboardData>> = await this.api.get(`/stores/${id}/dashboard`);
    return response.data;
  }

  // ============= FUNCIONÁRIOS =============

  async getEmployees(): Promise<ApiResponse<Employee[]>> {
    const response: AxiosResponse<ApiResponse<Employee[]>> = await this.api.get('/employees');
    return response.data;
  }

  async getStoreEmployees(storeId: number): Promise<ApiResponse<Employee[]>> {
    const response: AxiosResponse<ApiResponse<Employee[]>> = await this.api.get(`/stores/${storeId}/employees`);
    return response.data;
  }

  async getEmployee(id: number): Promise<ApiResponse<Employee>> {
    const response: AxiosResponse<ApiResponse<Employee>> = await this.api.get(`/employees/${id}`);
    return response.data;
  }

  async createEmployee(data: EmployeeFormData): Promise<ApiResponse<Employee>> {
    const response: AxiosResponse<ApiResponse<Employee>> = await this.api.post('/employees', data);
    return response.data;
  }

  async updateEmployee(id: number, data: Partial<EmployeeFormData>): Promise<ApiResponse<Employee>> {
    const response: AxiosResponse<ApiResponse<Employee>> = await this.api.put(`/employees/${id}`, data);
    return response.data;
  }

  async deleteEmployee(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/employees/${id}`);
    return response.data;
  }

  async toggleEmployeeStatus(id: number): Promise<ApiResponse<Employee>> {
    const response: AxiosResponse<ApiResponse<Employee>> = await this.api.post(`/employees/${id}/toggle-status`);
    return response.data;
  }

  // ============= CLIENTES =============

  async getCustomers(): Promise<ApiResponse<Customer[]>> {
    const response: AxiosResponse<ApiResponse<Customer[]>> = await this.api.get('/customers');
    return response.data;
  }

  async getStoreCustomers(storeId: number): Promise<ApiResponse<Customer[]>> {
    const response: AxiosResponse<ApiResponse<Customer[]>> = await this.api.get(`/stores/${storeId}/customers`);
    return response.data;
  }

  async getCustomer(id: number): Promise<ApiResponse<Customer>> {
    const response: AxiosResponse<ApiResponse<Customer>> = await this.api.get(`/customers/${id}`);
    return response.data;
  }

  async createCustomer(data: CustomerFormData): Promise<ApiResponse<Customer>> {
    try {
      const response: AxiosResponse<ApiResponse<Customer>> = await this.api.post('/customers', data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Erro ao salvar cliente',
          errors: error.response.data.errors || {}
        };
      }
      throw error;
    }
  }

  async updateCustomer(id: number, data: Partial<CustomerFormData>): Promise<ApiResponse<Customer>> {
    try {
      const response: AxiosResponse<ApiResponse<Customer>> = await this.api.put(`/customers/${id}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Erro ao salvar cliente',
          errors: error.response.data.errors || {}
        };
      }
      throw error;
    }
  }

  async deleteCustomer(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/customers/${id}`);
    return response.data;
  }

  async getCustomerPointsHistory(id: number): Promise<ApiResponse<PointsTransaction[]>> {
    const response: AxiosResponse<ApiResponse<PointsTransaction[]>> = await this.api.get(`/customers/${id}/points-history`);
    return response.data;
  }

  // ============= PRÊMIOS =============

  async getRewards(): Promise<ApiResponse<Reward[]>> {
    const response: AxiosResponse<ApiResponse<Reward[]>> = await this.api.get('/rewards');
    return response.data;
  }

  async getStoreRewards(storeId: number): Promise<ApiResponse<Reward[]>> {
    const response: AxiosResponse<ApiResponse<Reward[]>> = await this.api.get(`/stores/${storeId}/rewards`);
    return response.data;
  }

  async getReward(id: number): Promise<ApiResponse<Reward>> {
    const response: AxiosResponse<ApiResponse<Reward>> = await this.api.get(`/rewards/${id}`);
    return response.data;
  }

  async createReward(data: RewardFormData): Promise<ApiResponse<Reward>> {
    const response: AxiosResponse<ApiResponse<Reward>> = await this.api.post('/rewards', data);
    return response.data;
  }

  async updateReward(id: number, data: Partial<RewardFormData>): Promise<ApiResponse<Reward>> {
    const response: AxiosResponse<ApiResponse<Reward>> = await this.api.put(`/rewards/${id}`, data);
    return response.data;
  }

  async deleteReward(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/rewards/${id}`);
    return response.data;
  }

  async toggleRewardStatus(id: number): Promise<ApiResponse<Reward>> {
    const response: AxiosResponse<ApiResponse<Reward>> = await this.api.post(`/rewards/${id}/toggle-status`);
    return response.data;
  }

  // ============= SISTEMA DE PONTOS =============

  async addPoints(data: AddPointsData): Promise<ApiResponse<any>> {
    const { customer_id, ...payload } = data;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/customers/${customer_id}/add-points`, payload);
    return response.data;
  }

  async updateStorePointsSettings(storeId: number, data: StorePointsSettingsData): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/stores/${storeId}/points-settings`, data);
    return response.data;
  }

  async redeemReward(data: RedeemRewardData): Promise<ApiResponse<PointsTransaction>> {
    const response: AxiosResponse<ApiResponse<PointsTransaction>> = await this.api.post('/points/redeem', data);
    return response.data;
  }

  async adjustPoints(data: { customer_id: number; points: number; description: string }): Promise<ApiResponse<PointsTransaction>> {
    const response: AxiosResponse<ApiResponse<PointsTransaction>> = await this.api.post('/points/adjust', data);
    return response.data;
  }

  async getPointsTransactions(params?: any): Promise<ApiResponse<PointsTransaction[]>> {
    const response: AxiosResponse<ApiResponse<PointsTransaction[]>> = await this.api.get('/points/transactions', { params });
    return response.data;
  }

  async getTransactionDetails(id: number): Promise<ApiResponse<PointsTransaction>> {
    const response: AxiosResponse<ApiResponse<PointsTransaction>> = await this.api.get(`/points/transactions/${id}`);
    return response.data;
  }

  // ============= RELATÓRIOS =============

  async getPointsSummaryReport(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/points/reports/summary', { params });
    return response.data;
  }

  async getPointsPeriodReport(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/points/reports/by-period', { params });
    return response.data;
  }

  async getEmployeeReport(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/points/reports/by-employee', { params });
    return response.data;
  }

  async getCustomerReport(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/points/reports/by-customer', { params });
    return response.data;
  }

  async getAdvancedReports(params?: { start_date?: string; end_date?: string; months?: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/reports/advanced', { params });
    return response.data;
  }

  async getCustomersRanking(storeId: number): Promise<ApiResponse<Customer[]>> {
    const response: AxiosResponse<ApiResponse<Customer[]>> = await this.api.get(`/reports/customers-ranking/${storeId}`);
    return response.data;
  }

  async getRewardsPerformance(storeId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/reports/rewards-performance/${storeId}`);
    return response.data;
  }

  // ============= ADMIN AUTH =============

  async adminLogin(credentials: { email: string; password: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/login', credentials);
    return response.data;
  }

  async adminLogout(): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/admin/logout');
    return response.data;
  }

  async adminMe(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/me');
    return response.data;
  }

  async adminUpdateProfile(data: {
    name: string;
    email: string;
    current_password?: string;
    new_password?: string;
    new_password_confirmation?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/admin/profile', data);
    return response.data;
  }

  // ============= COMPANIES (ADMIN) =============

  async getCompanies(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/companies', { params });
    return response.data;
  }

  async getCompany(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/companies/${id}`);
    return response.data;
  }

  async createCompany(data: any): Promise<ApiResponse<any>> {
    if (data?.logo instanceof File) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === 'logo') {
          if (value instanceof File) {
            formData.append('logo', value);
          }
          return;
        }
        formData.append(key, String(value));
      });
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/companies', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }

    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/companies', data);
    return response.data;
  }

  async updateCompany(id: number, data: any): Promise<ApiResponse<any>> {
    if (data?.logo instanceof File) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === 'logo') {
          if (value instanceof File) {
            formData.append('logo', value);
          }
          return;
        }
        formData.append(key, String(value));
      });
      formData.append('_method', 'PUT');
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/admin/companies/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }

    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/admin/companies/${id}`, data);
    return response.data;
  }

  async deleteCompany(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/admin/companies/${id}`);
    return response.data;
  }

  async suspendCompany(id: number, reason: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/admin/companies/${id}/suspend`, { reason });
    return response.data;
  }

  async activateCompany(id: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/admin/companies/${id}/activate`);
    return response.data;
  }

  async getCompaniesStatistics(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/companies/statistics');
    return response.data;
  }

  // ============= ADMIN DASHBOARD =============

  async getAdminDashboardStatistics(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/dashboard/statistics');
    return response.data;
  }

  async getAdminDashboardRecentCompanies(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/dashboard/recent-companies');
    return response.data;
  }

  async getAdminDashboardRecentActivity(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/dashboard/recent-activity');
    return response.data;
  }

  async getAdminDashboardMonthlyRevenue(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/dashboard/monthly-revenue');
    return response.data;
  }

  async getAdminDashboardPlanDistribution(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/dashboard/plan-distribution');
    return response.data;
  }

  // ============= ADMIN FINANCIAL =============

  async getAdminFinancialStatistics(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/financial/statistics', { params });
    return response.data;
  }

  async getAdminFinancialTransactions(params?: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/financial/transactions', { params });
    return response.data;
  }

  async getAdminFinancialMonthlyRevenue(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/financial/monthly-revenue');
    return response.data;
  }

  // ============= ACCOUNT CREDITS =============

  async getCreditsBalance(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/credits/balance');
    return response.data;
  }

  async getCreditsHistory(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/credits/history');
    return response.data;
  }

  async getCreditDetails(creditId: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/credits/${creditId}`);
    return response.data;
  }

  // ============= PLAN LIMITS =============

  async getPlanLimits(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/plan-limits');
    return response.data;
  }

  async getPlanUsage(storeId?: number): Promise<ApiResponse<any>> {
    const params = storeId ? { store_id: storeId } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/plan-usage', { params });
    return response.data;
  }

  // ============= PAYMENT SETTINGS =============

  async getPaymentSettings(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/payment-settings');
    return response.data;
  }

  async updatePaymentSettingsBatch(data: { settings: Array<{ id: number; value: string }> }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/admin/payment-settings/batch', data);
    return response.data;
  }

  async testPaymentConnection(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/payment-settings/test-connection');
    return response.data;
  }

  // ============= EMAIL SETTINGS =============

  async getEmailSettings(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/email-settings');
    return response.data;
  }

  async updateEmailSettingsBatch(data: { settings: Array<{ id: number; value: string }> }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/admin/email-settings/batch', data);
    return response.data;
  }

  async testEmailConnection(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/email-settings/test-connection');
    return response.data;
  }

  // ============= SYSTEM SETTINGS =============

  async getSystemSettings(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/system-settings');
    return response.data;
  }

  async updateSystemSettingsBatch(data: { settings: Array<{ key: string; value: string | null }> }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/admin/system-settings/batch', data);
    return response.data;
  }

  // ============= BACKUP =============

  async listBackups(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/backup/list');
    return response.data;
  }

  async createBackup(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/backup/create');
    return response.data;
  }

  async restoreBackup(data: FormData): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/backup/restore', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async downloadBackup(filename: string): Promise<AxiosResponse<Blob>> {
    return this.api.get(`/admin/backup/download/${filename}`, {
      responseType: 'blob',
    });
  }

  // ============= PAYMENTS =============

  async createSubscription(data: { plan_id: number; billing_cycle: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/payments/subscription', data);
    return response.data;
  }

  async createPayment(data: { plan_id: number; description?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/payments/payment', data);
    return response.data;
  }

  async cancelSubscription(data: { subscription_id: number }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/payments/subscription/cancel', data);
    return response.data;
  }

  async getSubscriptions(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/payments/subscriptions');
    return response.data;
  }

  async getPayments(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/payments/payments');
    return response.data;
  }

  async getSubscriptionStatus(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/payments/subscription/status');
    return response.data;
  }

  // ============= UTILS =============

  setAuthToken(token: string): void {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  removeAuthToken(): void {
    delete this.api.defaults.headers.common['Authorization'];
  }

  // Expor instância axios para casos especiais
  get axiosInstance(): AxiosInstance {
    return this.api;
  }
}

export const apiService = new ApiService();
export default apiService;

// Exportações específicas para facilitar o uso
export const authService = apiService;
export const planService = apiService;
export const storeService = apiService;
export const employeeService = apiService;
export const customerService = apiService;
export const rewardService = apiService;
export const pointsService = apiService;

// Exportar instância axios para uso direto
export const api = apiService.axiosInstance;
